/**
 * FASE 4.2 — Area Cliente Unificata.
 * LoginModal: modale auth con flusso OTP via email/telefono.
 * Riusa endpoint esistenti /api/client/* SENZA navigare via.
 *
 * Stati:
 *   - "credentials" → user inserisce email + password (o telefono → OTP)
 *   - "otp"         → user inserisce codice OTP
 *   - "register"    → form registrazione (apre RegisterModal)
 *   - "reset"       → invio link reset password
 */
import { useState } from "react";
import { X, Mail, Lock, Loader2, ArrowRight, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useClientAuth } from "@/contexts/ClientAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;

export default function LoginModal({ open, onClose, onSwitchToRegister, initialMode = "credentials" }) {
  const { login: setSession } = useClientAuth();
  const [mode, setMode] = useState(initialMode); // credentials | otp | reset | reset_confirm
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [debugCode, setDebugCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const reset = () => {
    setMode("credentials"); setEmail(""); setPassword(""); setOtpCode("");
    setResetCode(""); setNewPassword(""); setDebugCode(null); setError(null);
  };
  const close = () => { reset(); onClose(); };

  const onLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/login`, { email, password });
      // Backend ritorna: {account_id, needs_otp:true, otp_test_mode, otp_debug_code}
      if (data?.needs_otp) {
        setDebugCode(data?.otp_debug_code || null);
        setMode("otp");
        toast.success(`OTP inviato${data?.otp_debug_code ? " (TEST: " + data.otp_debug_code + ")" : ""}`);
      } else if (data?.token || data?.access_token) {
        // login senza OTP (eventuale flusso futuro)
        setSession(data.token || data.access_token, data.account || null);
        toast.success("Accesso effettuato");
        close();
      } else {
        toast.error("Risposta inattesa dal server");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || "Credenziali non valide"));
    } finally { setLoading(false); }
  };

  const onOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, {
        email, code: otpCode, purpose: "login",
      });
      const tok = data?.token;
      if (tok) {
        setSession(tok, { id: data.account_id, email });
        toast.success("Bentornato/a!");
        close();
      } else {
        setError("Token mancante nella risposta");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || "OTP non valido"));
    } finally { setLoading(false); }
  };

  const onResetRequest = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/password/reset-request`, { email });
      setDebugCode(data?.debug_code || null);
      toast.success("Email inviata (controlla casella)");
      setMode("reset_confirm");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Errore richiesta");
    } finally { setLoading(false); }
  };

  const onResetConfirm = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/password/reset-confirm`, {
        email, code: resetCode, new_password: newPassword,
      });
      toast.success("Password aggiornata, accedi ora");
      setMode("credentials"); setPassword(newPassword);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Errore");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      data-testid="login-modal-overlay"
      onClick={close}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="login-modal"
      >
        <button onClick={close}
          className="absolute right-3 top-3 p-1.5 rounded-full hover:bg-zinc-100"
          data-testid="login-modal-close" aria-label="Chiudi">
          <X className="w-4 h-4 text-zinc-500" />
        </button>

        <header className="mb-4">
          <h2 className="text-2xl font-semibold text-[#163321]">
            {mode === "reset" || mode === "reset_confirm" ? "Recupera Password" :
              mode === "otp" ? "Verifica OTP" : "Accedi"}
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            {mode === "credentials" && "Accedi alla tua Area Cliente Colucci"}
            {mode === "otp" && `Inserisci il codice ricevuto via SMS al numero associato a ${email}`}
            {mode === "reset" && "Ti invieremo un codice via email per reimpostare la password"}
            {mode === "reset_confirm" && "Inserisci il codice ricevuto e la nuova password"}
          </p>
        </header>

        {/* CREDENZIALI */}
        {mode === "credentials" && (
          <form onSubmit={onLoginSubmit} className="space-y-3" data-testid="login-form">
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Email</span>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} required
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="email@esempio.it" data-testid="login-email" />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Password</span>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12}
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="••••••••••••" data-testid="login-password" />
              </div>
              <button type="button" onClick={() => { setMode("reset"); setError(null); }}
                className="text-[11px] text-emerald-700 hover:underline mt-1" data-testid="login-forgot-pw">
                Password dimenticata?
              </button>
            </label>
            {error && <p className="text-xs text-rose-600" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading || !email || !password}
              className="w-full bg-[#163321] text-white font-semibold py-2.5 rounded-lg hover:bg-[#1d4a2e] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Continua
            </button>
            <p className="text-xs text-center text-zinc-500 pt-2 border-t border-zinc-100">
              Non sei ancora registrato?{" "}
              <button type="button" onClick={() => { onSwitchToRegister?.(); close(); }}
                className="text-emerald-700 font-medium hover:underline" data-testid="login-goto-register">
                Registrati
              </button>
            </p>
          </form>
        )}

        {/* OTP */}
        {mode === "otp" && (
          <form onSubmit={onOtpSubmit} className="space-y-3" data-testid="login-otp-form">
            {debugCode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-900" data-testid="login-otp-debug">
                <strong>TEST_MODE:</strong> codice <code className="font-mono">{debugCode}</code>
              </div>
            )}
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Codice OTP (6 cifre)</span>
              <div className="relative mt-1">
                <Phone className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="text" inputMode="numeric" pattern="[0-9]{6}" value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-lg font-mono tracking-widest text-center focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="000000" data-testid="login-otp-code" />
              </div>
            </label>
            {error && <p className="text-xs text-rose-600" data-testid="login-otp-error">{error}</p>}
            <button type="submit" disabled={loading || otpCode.length !== 6}
              className="w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-otp-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Accedi
            </button>
            <button type="button" onClick={() => setMode("credentials")}
              className="block w-full text-xs text-zinc-500 hover:text-zinc-700 mt-2">
              ← Torna indietro
            </button>
          </form>
        )}

        {/* RESET REQUEST */}
        {mode === "reset" && (
          <form onSubmit={onResetRequest} className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} required
                className="w-full px-3 py-2 mt-1 border border-zinc-300 rounded-lg text-sm" />
            </label>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            <button type="submit" disabled={loading || !email}
              className="w-full bg-[#163321] text-white font-semibold py-2.5 rounded-lg hover:bg-[#1d4a2e] disabled:opacity-50">
              {loading ? "..." : "Invia codice"}
            </button>
            <button type="button" onClick={() => setMode("credentials")}
              className="block w-full text-xs text-zinc-500 hover:text-zinc-700 mt-2">← Annulla</button>
          </form>
        )}

        {/* RESET CONFIRM */}
        {mode === "reset_confirm" && (
          <form onSubmit={onResetConfirm} className="space-y-3">
            {debugCode && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs"><strong>TEST:</strong> {debugCode}</div>}
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Codice ricevuto</span>
              <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required
                className="w-full px-3 py-2 mt-1 border border-zinc-300 rounded-lg text-sm font-mono tracking-widest text-center" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Nuova password (min 12 char)</span>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={12}
                className="w-full px-3 py-2 mt-1 border border-zinc-300 rounded-lg text-sm" />
            </label>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            <button type="submit" disabled={loading || resetCode.length !== 6 || newPassword.length < 12}
              className="w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
              {loading ? "..." : "Reimposta password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
